import React, { useEffect, useState, useMemo } from "react";
import { Button, Modal, Input, Space } from "antd";
import { useLinkProfile } from "../api/useLinkProfile";
import { LinkProfile, LinkButton } from "../models";
import { colorToHex } from "../utils/helper";
import { MarqueeButtonText } from "./MarqueeButtonText";

const defaultButtonStyles = {
  fontSize: "1.2rem",
  color: "#000000",
  backgroundColor: "rgba(16, 185, 129, 0.5)",
};

interface Props {
  profileIn: LinkProfile;
}

export const PageEditor: React.FC<Props> = ({ profileIn }) => {
  const { updateLinkProfile } = useLinkProfile();
  const [profile, setProfile] = useState<LinkProfile>(profileIn);

  // Video URL logic from DymanicOverlay
  let videoURL: string | null = null;
  if (profile?.video_link) {
    const url = new URL(profile.video_link);
    url.searchParams.set("autoplay", "1");
    url.searchParams.set("loop", "1");
    url.searchParams.set("rel", "0");
    url.searchParams.set("native_context_menu", "0");
    url.searchParams.set("controls", "0");
    url.searchParams.set("progress_bar", "0");
    url.searchParams.set("fullscreen_button", "0");
    url.searchParams.set("timestamp", "0");
    url.searchParams.set("play_button", "0");
    url.searchParams.set("volume_control", "0");
    url.searchParams.set("closed_caption", "0");
    videoURL = url.toString();
  }

  useEffect(() => {
    setProfile(profileIn);
    setButtonStyles({
      fontSize: defaultButtonStyles.fontSize,
      color: colorToHex(profileIn.text_color || defaultButtonStyles.color),
      backgroundColor:
        profileIn.background_color || defaultButtonStyles.backgroundColor,
    });
  }, [profileIn]);

  const [showDialog, setShowDialog] = useState(false);
  const [buttonStyles, setButtonStyles] = useState(defaultButtonStyles);
  const [editingButton, setEditingButton] = useState<LinkButton | null>(null);
  const [editingButtonText, setEditingButtonText] = useState("");
  const [editingButtonUrl, setEditingButtonUrl] = useState("");
  const [editingButtonTextColor, setEditingButtonTextColor] = useState("");
  const [editingButtonBackgroundColor, setEditingButtonBackgroundColor] = useState("");

  // Get buttons sorted by order
  const sortedButtons = useMemo(() => {
    const fixedButton = {
      order: 0,
      url: profile?.guide_link || "",
      text: profile?.guide_text || "Order Now",
      background_color: profile?.background_color || defaultButtonStyles.backgroundColor,
      text_color: profile?.text_color || defaultButtonStyles.color,
    } as LinkButton;

    if (profile?.buttons) {
      return [fixedButton, ...profile.buttons].sort((a, b) => a.order - b.order);
    }

    return [fixedButton];
  }, [profile?.buttons, profile?.guide_link, profile?.guide_text, profile?.background_color, profile?.text_color]);

  // Get buttons based on layout
  const getButtonsForLayout = useMemo(() => {
    const layout = profile?.button_layout ?? 0;
    const buttons = sortedButtons;

    switch (layout) {
      case 0: // Single button
        return { top: [buttons[0]], bottom: [] };
      case 1: // Two buttons side by side
        return { top: buttons.slice(0, 2), bottom: [] };
      case 2: // Two buttons stacked
        return { top: [buttons[0]], bottom: [buttons[1]] };
      case 3: // Two buttons on top, one below
        return { top: buttons.slice(0, 2), bottom: [buttons[2]] };
      case 4: // Two buttons on top, two below
        return { top: buttons.slice(0, 2), bottom: buttons.slice(2, 4) };
      default:
        return { top: [buttons[0]], bottom: [] };
    }
  }, [profile?.button_layout, sortedButtons]);

  const handleOpenDialog = (button: LinkButton) => {
    setEditingButton(button);
    setEditingButtonText(button.text || "");
    setEditingButtonUrl(button.url || "");
    setEditingButtonTextColor(colorToHex(button.text_color || buttonStyles.color));
    setEditingButtonBackgroundColor(colorToHex(button.background_color || buttonStyles.backgroundColor));
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingButton(null);
  };

  const handleApply = () => {
    if (!editingButton) return;

    // Update the button in the buttons array
    const updatedButtons = profile.buttons ? [...profile.buttons] : [];

    if (editingButton.order === 0) {
      profile.guide_text = editingButtonText;
      profile.guide_link = editingButtonUrl;
      profile.text_color = editingButtonTextColor;
      profile.background_color = editingButtonBackgroundColor;
    } else {
      const updatedButton: LinkButton = {
        ...editingButton,
        text: editingButtonText,
        url: editingButtonUrl,
        text_color: editingButtonTextColor,
        background_color: editingButtonBackgroundColor,
      };
  
      const buttonIndex = updatedButtons.findIndex(b => b.order === editingButton.order);
      updatedButtons[buttonIndex] = updatedButton;
    }

      // Sort buttons by order
      updatedButtons.sort((a, b) => a.order - b.order);
      // If editing button 0 (the fixed button), also update guide_text and guide_link
      const updatedProfile: LinkProfile = {
        ...profile,
        buttons: updatedButtons,
      };

    // Update local state immediately for UI feedback
    setProfile(updatedProfile);
    
    // Save to backend
    updateLinkProfile(updatedProfile);
    setShowDialog(false);
    setEditingButton(null);
  };

  const renderButton = (button: LinkButton, index: number, isTop: boolean, rowLength: number) => {
    if (!button) return null;

    const bgColor = button.background_color || buttonStyles.backgroundColor;
    const textColor = button.text_color || buttonStyles.color;
    const isTwoButtonsInRow = rowLength === 2;

    return (
      <Button
        key={`${button.order}-${index}`}
        className={`order-button-${button.order}`}
        style={{
          width: isTwoButtonsInRow ? "45%" : "98%",
          height: "clamp(3rem, 8vh, 4rem)",
          backgroundColor: bgColor,
          color: textColor,
          fontSize: buttonStyles.fontSize,
          fontWeight: "bold",
          borderRadius: "9px",
          boxShadow:
            "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
          transition: "background-color 300ms",
          pointerEvents: "auto",
          border: "none",
          zIndex: 2,
          margin: isTwoButtonsInRow ? "0 1%" : "0",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingLeft: "0.75rem",
          paddingRight: "0.75rem",
        }}
        onClick={() => {
          // Always open edit dialog when button is clicked
          handleOpenDialog(button);
        }}
        onMouseOver={(e) => {
          // Try to brighten the color (works for hex and rgb(a))
          let bg = bgColor;
          let bright = bg;
          if (bg.startsWith("#")) {
            // hex color
            const hex = bg.replace("#", "");
            if (hex.length === 3 || hex.length === 6) {
              let num = parseInt(
                hex.length === 3
                  ? hex
                      .split("")
                      .map((x) => x + x)
                      .join("")
                  : hex,
                16
              );
              let r = Math.min(255, ((num >> 16) & 0xff) + 40);
              let g = Math.min(255, ((num >> 8) & 0xff) + 40);
              let b = Math.min(255, (num & 0xff) + 40);
              bright = `rgb(${r},${g},${b})`;
            }
          } else if (bg.startsWith("rgb")) {
            // rgb or rgba
            const nums = bg.match(/[\d.]+/g);
            if (nums) {
              let [r, g, b, a] = nums.map(Number);
              r = Math.min(255, r + 40);
              g = Math.min(255, g + 40);
              b = Math.min(255, b + 40);
              bright =
                a !== undefined
                  ? `rgba(${r},${g},${b},${a})`
                  : `rgb(${r},${g},${b})`;
            }
          }
          e.currentTarget.style.backgroundColor = bright;
        }}
        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = bgColor)}
      >
        <MarqueeButtonText text={button.text || "Button"} />
      </Button>
    );
  };

  if (!profileIn.id) {
    return <div className="bg-black w-full h-full">Preview Panel</div>;
  }

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        backgroundColor: "#000",
      }}
    >
      {/* Video background */}
      {videoURL && (
        <iframe
          src={videoURL}
          allowFullScreen={false}
          title="Profile Video"
          style={{
            width: "100%",
            height: "100%",
            zIndex: 0,
            pointerEvents: "none",
            background: "black",
          }}
          allow="autoplay; encrypted-media;"
        />
      )}

      {/* Buttons overlay */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          paddingBottom: "max(1rem, 2vh)",
          paddingLeft: "max(2rem, 4vh)",
          paddingRight: "max(2rem, 4vh)",

          width: "100%",
          height: "auto",
          zIndex: 5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        {/* Top row buttons */}
        {getButtonsForLayout.top.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              gap: getButtonsForLayout.top.length === 2 ? "2%" : "0",
              marginBottom:
                getButtonsForLayout.bottom.length > 0
                  ? "clamp(1rem, 2vh, 1.5rem)"
                  : "0",
            }}
          >
            {getButtonsForLayout.top.map((button, index) =>
              renderButton(button, index, true, getButtonsForLayout.top.length)
            )}
          </div>
        )}

        {/* Bottom row buttons */}
        {getButtonsForLayout.bottom.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              gap: getButtonsForLayout.bottom.length === 2 ? "2%" : "0",
            }}
          >
            {getButtonsForLayout.bottom.map((button, index) =>
              renderButton(button, index, false, getButtonsForLayout.bottom.length)
            )}
          </div>
        )}

        {/* About button (if exists) */}
        {profile?.about_link && (
          <Button
            className="about-button"
            style={{
              position: "absolute",
              top: "calc(100% + 40px)",
              right: 0,
              backgroundColor: "rgba(59, 130, 246, 0.4)",
              color: "white",
              padding: "8px 12px",
              borderRadius: "4px",
              fontSize: "clamp(12px, 1.5vw, 14px)",
              whiteSpace: "nowrap",
              border: "1px solid rgba(255, 255, 255, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 300ms",
              pointerEvents: "auto",
              zIndex: 2,
              marginBottom: 12,
            }}
            onClick={() =>
              profile?.about_link && (window.location.href = profile.about_link)
            }
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "rgba(59, 130, 246, 0.9)")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "rgba(59, 130, 246, 0.4)")
            }
          >
            {profile?.about_text || "About"}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginLeft: "4px" }}
            >
              <path
                d="M5 12H19"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 5L19 12L12 19"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>
        )}
        <Modal
          title={`Edit Button ${editingButton ? editingButton.order + 1 : ""} Style`}
          open={showDialog}
          onCancel={handleCloseDialog}
          onOk={handleApply}
          okText="Apply"
          cancelText="Cancel"
        >
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <div>
              <label style={{ fontWeight: 500 }}>Button Text</label>
              <Input
                value={editingButtonText}
                onChange={(e) => setEditingButtonText(e.target.value)}
                placeholder="Enter button text"
                maxLength={32}
                style={{ marginTop: 4 }}
              />
            </div>
            <div>
              <label style={{ fontWeight: 500 }}>Button URL</label>
              <Input
                value={editingButtonUrl}
                onChange={(e) => setEditingButtonUrl(e.target.value)}
                placeholder="Enter button URL"
                type="url"
                style={{ marginTop: 4 }}
              />
            </div>
            <div>
              <label style={{ fontWeight: 500 }}>Text Color</label>
              <Input
                type="color"
                value={editingButtonTextColor}
                onChange={(e) => setEditingButtonTextColor(e.target.value)}
                style={{
                  width: 60,
                  height: 36,
                  marginLeft: 12,
                  verticalAlign: "middle",
                }}
              />
            </div>
            <div>
              <label style={{ fontWeight: 500 }}>Background Color</label>
              <Input
                type="color"
                value={editingButtonBackgroundColor}
                onChange={(e) => setEditingButtonBackgroundColor(e.target.value)}
                style={{
                  width: 60,
                  height: 36,
                  marginLeft: 12,
                  verticalAlign: "middle",
                }}
              />
            </div>
          </Space>
        </Modal>
      </div>
    </div>
  );
};
